import { useQuery } from 'react-query'

import { getLocalName, setLocalName } from '../../Chat/blocs/useLocalName'
import {
  getWebinarConnectInfo,
  useConnectInfoQuery,
} from '../../global/blocs/useConnectInfo'
import { getRoomInfo } from '../../global/blocs/roomInfo'
import { request } from '../utils/api'
import { HTTPMethod, type Response } from '../utils/api.types'
import queryClient from '../../queryClient'

type GetSpeakersRequest = {
  live_id: number
  token?: string
  include_unapproved_p?: boolean
  request_status?: RequestStatus
  creation_source?: CreationSource
}

type GetSpeakersResponse = Response & {
  data: SpeakerDTO[]
}

type RequestStatus = 'requested' | 'approved' | 'denied' | 'expired'
type CreationSource = 'admin' | 'guest'

type SpeakerDTO = {
  live_speaker_id: number | string
  name: string
  email: string
  full_name?: string
  title: string
  company: string
  bio: string
  hidden_p: boolean
  show_title_card_p: boolean
  avatar_url: string
  request_status: RequestStatus
  creation_source: CreationSource
  question: string
  guest_speaker_preview_link: string
  facebook: string
  link: string
  linkedin: string
  twitter: string
  last_login_role: string
}

type Speaker = {
  id: number
  name: string
  fullName: string
  email: string
  title?: string
  company?: string
  bio?: string
  isHidden: boolean
  isShowingTitleCard: boolean
  avatarURL?: string
  requestStatus: RequestStatus
  creationSource: CreationSource
  question?: string
  guestSpeakerPreviewLink: string
  facebook?: string
  link?: string
  linkedin?: string
  twitter?: string
  role?: string
}

type UseSpeakersQueryOptions = {
  includeUnapproved?: boolean
  refetchInterval?: number | false
  requestStatus?: RequestStatus
  creationSource?: CreationSource
}

function getSpeakers(
  liveId: number,
  token: string | undefined,
  includeUnapproved: boolean,
  requestStatus?: RequestStatus,
  creationSource?: CreationSource
) {
  return request<GetSpeakersRequest, GetSpeakersResponse>({
    url: '/api/2/live/speaker/list',
    method: HTTPMethod.POST,
    data: {
      live_id: liveId,
      token,
      include_unapproved_p: includeUnapproved,
      request_status: requestStatus,
      creation_source: creationSource,
    },
  })
}

function mapDTOToSpeaker(dto: SpeakerDTO): Speaker {
  return {
    id: +dto.live_speaker_id,
    name: dto.name,
    fullName: dto.full_name ?? dto.name,
    email: dto.email,
    title: dto.title !== '' ? dto.title : undefined,
    company: dto.company !== '' ? dto.company : undefined,
    bio: dto.bio !== '' ? dto.bio : undefined,
    isHidden: dto.hidden_p,
    isShowingTitleCard: dto.show_title_card_p,
    avatarURL: dto.avatar_url !== '' ? dto.avatar_url : undefined,
    requestStatus: dto.request_status,
    creationSource: dto.creation_source,
    question: dto.question !== '' ? dto.question : undefined,
    guestSpeakerPreviewLink: dto.guest_speaker_preview_link,
    facebook: dto.facebook !== '' ? dto.facebook : undefined,
    link: dto.link !== '' ? dto.link : undefined,
    linkedin: dto.linkedin !== '' ? dto.linkedin : undefined,
    twitter: dto.twitter !== '' ? dto.twitter : undefined,
    role: dto.last_login_role,
  }
}

const speakersQuery = (
  requestStatus?: RequestStatus,
  creationSource?: CreationSource,
  includeUnapproved: boolean = false
) => {
  return {
    queryKey: [
      'speakers',
      { includeUnapproved, requestStatus, creationSource },
    ],
    queryFn: async () => {
      const globalInfo = getRoomInfo()

      const token =
        globalInfo.role === 'audience'
          ? globalInfo.audienceToken
          : (await getWebinarConnectInfo()).token

      const { data } = await getSpeakers(
        globalInfo.liveId,
        token,
        includeUnapproved,
        requestStatus,
        creationSource
      )

      const speakers = data.map(mapDTOToSpeaker)

      // update comment name
      const localSpeakerId = getLocalName().speakerId
      if (localSpeakerId) {
        const localSpeaker = speakers.find((s) => s.id === localSpeakerId)
        if (localSpeaker) {
          setLocalName({
            speakerId: localSpeakerId,
            sender: localSpeaker.name,
            full: localSpeaker.fullName,
          })
        }
      }

      return speakers
    },
    refetchInterval: 10e3,
    initialData: [],
    initialDataUpdatedAt: 0,
    staleTime: 5 * 60e3,
    cacheTime: Infinity,
  }
}

const getSpeakersInfo = async (
  requestStatus?: RequestStatus,
  creationSource?: CreationSource,
  includeUnapproved: boolean = false
) => {
  return queryClient.fetchQuery<Speaker[]>(
    speakersQuery(requestStatus, creationSource, includeUnapproved)
  )
}

function useSpeakersQuery({
  includeUnapproved = false,
  requestStatus,
  creationSource,
}: UseSpeakersQueryOptions = {}) {
  return useQuery<Speaker[]>(
    speakersQuery(requestStatus, creationSource, includeUnapproved)
  )
}

const useVisibleWebinarSpeakers = () => {
  const { data: speakers } = useSpeakersQuery()
  return (
    speakers?.filter(
      (s) => s.creationSource !== 'guest' && s.isShowingTitleCard
    ) ?? []
  )
}

const useIsSpeakerGuest = () => {
  const { data: speakers } = useSpeakersQuery({
    requestStatus: 'approved',
    creationSource: 'guest',
  })

  const { data: connectionInfo } = useConnectInfoQuery()

  return speakers?.some((s) => s.id === connectionInfo?.live_speaker_id)
}

export default useSpeakersQuery

export {
  useSpeakersQuery,
  getSpeakersInfo,
  type SpeakerDTO,
  type Speaker,
  type UseSpeakersQueryOptions,
  mapDTOToSpeaker,
  useVisibleWebinarSpeakers,
  useIsSpeakerGuest,
}
